<template>
  <div class="card">
    <div class="card-header">
      <h5 class="card-title">Staff Vacations</h5>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-4">
        <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
        <span class="ms-2">Loading staff data...</span>
      </div>
      <div
        v-else-if="managedStaffList.length"
        class="table-responsive"
      >
        <table class="table table-striped table-hover">
          <thead>
            <tr>
              <th>Staff Name</th>
              <th>Staff Email</th>
              <th>Vacation</th>
              <th>Sick Leave</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="staff in sortedStaffList"
              :key="staff.id"
            >
              <td>
                <a :href="`/admin/vacation/staff-tracker/${staff.id}`">{{ staff.user.full_name }}</a>
              </td>
              <td>{{ staff.user.email }}</td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span class="small">
                    {{ staff.used_vacation_days }} / {{ staff.total_available_vacation_days }}
                  </span>
                  <div
                    class="progress flex-grow-1"
                    role="progressbar"
                    :aria-valuenow="vacationPercent(staff)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Vacation used"
                    style="height: 6px; min-width: 60px; max-width: 100px"
                  >
                    <div
                      class="progress-bar"
                      :class="progressBarClass(vacationPercent(staff))"
                      :style="{ width: Math.min(vacationPercent(staff), 100) + '%' }"
                    />
                  </div>
                </div>
              </td>
              <td>
                <div class="d-flex align-items-center gap-2">
                  <span class="small">
                    {{ staff.used_sick_leave_days }} / {{ staff.total_sick_leave_days }}
                  </span>
                  <div
                    class="progress flex-grow-1"
                    role="progressbar"
                    :aria-valuenow="sickPercent(staff)"
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-label="Sick leave used"
                    style="height: 6px; min-width: 60px; max-width: 100px"
                  >
                    <div
                      class="progress-bar"
                      :class="progressBarClass(sickPercent(staff))"
                      :style="{ width: Math.min(sickPercent(staff), 100) + '%' }"
                    />
                  </div>
                </div>
              </td>
              <td>
                <a
                  :href="`/admin/vacation/staff-tracker/${staff.id}`"
                  class="btn btn-sm btn-outline-primary"
                >
                  View
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <p class="text-muted mb-0">No staff found</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
    name: "ManagerVacationTracker",
    props: {
        managerId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            isLoading: true,
        };
    },
    computed: {
        ...mapGetters({
            managedStaffList: "vacation/managedStaffList",
        }),
        sortedStaffList() {
            return [...this.managedStaffList].sort((a, b) => {
                const remainA = (a.remain_annual_vacation_days ?? 0) + (a.remain_annual_sick_leave_days ?? 0);
                const remainB = (b.remain_annual_vacation_days ?? 0) + (b.remain_annual_sick_leave_days ?? 0);
                return remainA - remainB;
            });
        },
    },
    mounted() {
        this.fetchStaffs();
    },
    methods: {
        vacationUsed(staff) {
            return staff.used_vacation_days ?? 0;
        },
        sickUsed(staff) {
            return staff.used_sick_leave_days ?? 0;
        },
        vacationPercent(staff) {
            const total = staff.total_available_vacation_days ?? 0;
            if (total <= 0) return 0;
            const used = this.vacationUsed(staff);
            return (used / total) * 100;
        },
        sickPercent(staff) {
            const total = staff.total_sick_leave_days ?? 0;
            if (total <= 0) return 0;
            const used = this.sickUsed(staff);
            return (used / total) * 100;
        },
        progressBarClass(percent) {
            if (percent >= 90) return "bg-danger";
            if (percent >= 70) return "bg-warning";
            return "bg-success";
        },
        async fetchStaffs() {
            this.isLoading = true;
            try {
                await this.getManagedStaffList({ manager_id: this.managerId });
            } finally {
                this.isLoading = false;
            }
        },
        ...mapActions({
            getManagedStaffList: "vacation/getManagedStaffList",
        }),
    },
};
</script>
